import { Stack, Text, Title } from "@mantine/core";
import { useParams } from "@tanstack/react-router";

import { SettingsFrame } from "../../platform/shell/SettingsFrame";
import { TitledHeader } from "../../ui/TitledHeader";
import { CreateToken } from "./CreateToken";
import { TokenList } from "./TokenList";

/**
 * Settings' Access tokens tab: personal access tokens (proposal 6.1) for the team in the URL.
 * The API binds each token to one team, so the page that makes them is the team's. The form
 * stays on the page rather than in a popover (design-system.md 3.7): the secret shows once, and
 * a popover that closes on a click outside could hide it before "Done, hide it".
 */
export function TokensScreen() {
  const { teamId } = useParams({ from: "/signed-in/teams/$teamId/tokens" });
  return (
    <SettingsFrame teamId={teamId}>
      <AccessTokens teamId={teamId} />
    </SettingsFrame>
  );
}

/**
 * The Access tokens tab's body, wherever its frame is (Classic's Settings, or Pro's, whose page
 * has its own h1: there the tab's title is an h2).
 */
export function AccessTokens({ teamId, titleOrder = 1 }: { teamId: string; titleOrder?: 1 | 2 }) {
  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Title order={titleOrder} fz={18} fw={700}>
          Access tokens
        </Title>
        <Text size="sm" c="var(--cai-color-text-muted)" maw={640}>
          A personal access token lets a script or an agent call the Created.ai API as you, in
          this team only. Revoke a token as soon as nothing needs it.
        </Text>
      </Stack>
      <CreateToken teamId={teamId} />
      <div>
        <TitledHeader title="Your tokens in this team" />
        <TokenList teamId={teamId} />
      </div>
    </Stack>
  );
}
